import React, { useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import {
  useAddOrganization,
  useOrganization,
} from "../../hooks/useOrganization";
import { useCountry } from "../../hooks/useCountry";
import { useCountryCities } from "../../hooks/useCity";
import { toast } from "react-toastify";

const OrganizationForm = ({ open, onClose, formData, onInputChange }) => {
  const addOrganization = useAddOrganization();
  const { data: organizations } = useOrganization();
  const { data: countries } = useCountry();
  const { data: cities, refetch: refetchCities } = useCountryCities(
    formData.country_id
  );
  const [error, setError] = useState("");

  useEffect(() => {
    if (formData.country_id) {
      refetchCities();
    }
  }, [formData.country_id, refetchCities]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); 

    const isDuplicate = organizations?.some(
      (org) => org.organization_id === formData.organization_id
    );

    if (isDuplicate) {
      setError("Organization ID already exists. Please use a unique ID.");
      return;
    }
    try {
      await addOrganization.mutateAsync({
        ...formData,
        created_at: new Date().toISOString(),
      });
      toast.success("Organization added successfully!");
      onClose();
    } catch (error) {
      toast.error("Failed to add organization. Please try again.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className="bg-blueColor text-white py-4">
        <i className="fas fa-plus text-xl mx-2"></i>
        <span className="text-xl font-semibold">Add New Organization</span>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent className="mt-4">
          <div className="grid grid-cols-2 gap-4 p-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Name
              </label>
              <input
                name="name"
                value={formData.name}
                onChange={onInputChange}
                type="text"
                required
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Organization ID
              </label>
              <input
                name="organization_id"
                value={formData.organization_id}
                onChange={onInputChange}
                type="text"
                required
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {error && (
                <div className="mb-4 text-red-600 font-medium">{error}</div>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Email
              </label>
              <input
                name="email"
                value={formData.email}
                onChange={onInputChange}
                type="email"
                required
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Phone Number
              </label>
              <input
                name="phone_number"
                value={formData.phone_number}
                onChange={onInputChange}
                type="tel"
                required
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Contact Name
              </label>
              <input
                name="contact_name"
                value={formData.contact_name}
                onChange={onInputChange}
                type="text"
                required
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Registration Number
              </label>
              <input
                name="registration_number"
                value={formData.registration_number}
                onChange={onInputChange}
                type="text"
                required
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                APO Number
              </label>
              <input
                name="APO_number"
                value={formData.APO_number}
                onChange={onInputChange}
                type="text"
                required
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Country
              </label>
              <select
                name="country_id"
                value={formData.country_id}
                onChange={onInputChange}
                required
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              >
                <option value="">Select Country</option>
                {countries?.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                City
              </label>
              <select
                name="city_id"
                value={formData.city_id}
                onChange={onInputChange}
                required
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                disabled={!formData.country_id}
              >
                <option value="">Select City</option>
                {cities?.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-4 mt-6 p-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
            <button
              data-track="add-organization-button"
              data-module="Organizations"
              type="submit"
              className="px-4 py-2 bg-blueColor text-white rounded"
            >
              Add
            </button>
          </div>
        </DialogContent>
      </form>
    </Dialog>
  );
};

export default OrganizationForm;
